import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Recycle, Building2, Ship, UserCheck, Factory, MapPin } from 'lucide-react';
import Breadcrumb from '../components/layout/Breadcrumb';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import dashboardAPI from '../services/dashboard';
import WasteFlowChart from '../components/dashboard/charts/WasteFlowChart';
import GeographicChart from '../components/dashboard/charts/GeographicChart';
import OceanWasteChart from '../components/dashboard/charts/OceanWasteChart';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardStats, wasteAnalytics, employeeData] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getWasteAnalytics(),
        dashboardAPI.getEmployeeData()
      ]);
      
      setStats({
        ...dashboardStats,
        wasteAnalytics,
        employeeData
      });
      setRecentActivity(dashboardStats.recentActivity || []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading dashboard: {error}</p>
        <button 
          onClick={fetchDashboardData}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Districts',
      value: stats?.totalDistricts || 0,
      change: 'Geographic Coverage',
      changeType: 'neutral',
      icon: MapPin,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      title: 'Active Centers',
      value: stats?.totalCenters || 0,
      change: `${stats?.totalPorts || 0} Ports`,
      changeType: 'neutral',
      icon: Building2,
      color: 'text-green-600 bg-green-100'
    },
    {
      title: 'Total Customers',
      value: stats?.totalCustomers || 0,
      change: `${stats?.totalFishermen || 0} Fishermans`,
      changeType: 'neutral',
      icon: Users,
      color: 'text-purple-600 bg-purple-100'
    },
    {
      title: 'Total Employees',
      value: stats?.totalEmployees || 0,
      change: 'Workforce',
      changeType: 'neutral',
      icon: UserCheck,
      color: 'text-orange-600 bg-orange-100'
    },
    {
      title: 'Aggregated Waste',
      value: `${stats?.totalAggregatedWaste || 0} kg`,
      change: `${stats?.availableWaste || 0} kg available`,
      changeType: 'positive',
      icon: Recycle,
      color: 'text-emerald-600 bg-emerald-100'
    },
    {
      title: 'Waste Revenue',
      value: `₹${(stats?.totalRevenue || 0).toLocaleString()}`,
      change: `${stats?.wasteUtilizationRate || 0}% utilization`,
      changeType: 'positive',
      icon: TrendingUp,
      color: 'text-indigo-600 bg-indigo-100'
    },
    {
      title: 'Ocean Collections',
      value: stats?.totalOceanCollections || 0,
      change: 'Marine waste collected',
      changeType: 'positive',
      icon: Ship,
      color: 'text-cyan-600 bg-cyan-100'
    },
    {
      title: 'Processing Centers',
      value: stats?.totalCenters || 0,
      change: 'Operational facilities',
      changeType: 'neutral',
      icon: Factory,
      color: 'text-gray-600 bg-gray-100'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb />

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">BaeruCRM Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Comprehensive waste management and ocean conservation overview
        </p>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <Card key={stat.title} className="hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
                <p className={`text-xs ${
                  stat.changeType === 'positive' ? 'text-green-600' : 
                  stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {stat.change}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Ocean Waste Breakdown */}
      {stats?.oceanWasteBreakdown && (
        <Card title="Ocean Waste Collection Breakdown" subtitle="Types of waste collected from ocean">
          <OceanWasteChart data={stats.oceanWasteBreakdown} />
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Waste Flow Analytics" subtitle="Aggregation → Processing → Sale">
          <WasteFlowChart data={{
            totalInput: stats?.totalAggregatedWaste || 0,
            totalOutput: stats?.totalSoldWaste || 0,
            currentStock: stats?.availableWaste || 0,
            utilizationRate: stats?.wasteUtilizationRate || 0
          }} />
        </Card>

        <Card title="Geographic Distribution" subtitle="Centers and ports by district">
          <GeographicChart data={stats?.geographicData} />
        </Card>
      </div>

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <Card title="Recent Activity" subtitle="Latest waste management activities">
          <div className="space-y-3">
            {recentActivity.slice(0, 8).map((activity) => (
              <div key={activity.id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl mr-3">{activity.icon}</div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(activity.timestamp).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  activity.type === 'waste_sold' ? 'bg-green-100 text-green-800' :
                  activity.type === 'ocean_collection' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {activity.type.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      <Card title="Quick Actions" subtitle="Common management tasks">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <Building2 className="h-8 w-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Manage Centers</h3>
            <p className="text-sm text-gray-600">Add, edit centers and track waste processing</p>
          </button>
          
          <button className="p-4 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <Ship className="h-8 w-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Ocean Collections</h3>
            <p className="text-sm text-gray-600">Monitor fishermans and ocean waste collection</p>
          </button>
          
          <button className="p-4 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <TrendingUp className="h-8 w-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Analytics & Reports</h3>
            <p className="text-sm text-gray-600">Detailed waste analytics and revenue reports</p>
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;